import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getCurrentUser, isAdmin } from '../utils/userAuth';
import { getRentals, addRental, deleteRental, getAllRentalsAdmin, adminDeleteListing } from '../utils/listingsStore';
import './Rentals.css';

function Rentals() {
  const navigate = useNavigate();
  const [rentals, setRentals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [activeTab, setActiveTab] = useState('browse');
  const [user, setUser] = useState(null);
  const [filters, setFilters] = useState({
    search: '',
    rentalItemType: '',
    rentalPeriod: '',
    sort: 'createdAt'
  });
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    rentalItemType: 'textbook',
    rentalPeriod: 'daily',
    price: '',
    deposit: '',
    location: 'Campus'
  });

  useEffect(() => {
    const currentUser = getCurrentUser();
    setUser(currentUser);
    fetchRentals();
  }, [filters, activeTab]);

  const fetchRentals = () => {
    setLoading(true);
    try {
      let data;
      if (activeTab === 'admin' && user && isAdmin(user.email)) {
        data = getAllRentalsAdmin();
      } else if (activeTab === 'my-listings' && user) {
        data = getRentals(filters).filter(r => r.owner?.id === user.id);
      } else {
        data = getRentals(filters);
      }
      setRentals(data);
    } catch (error) {
      console.error('Error fetching rentals:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    setFilters({
      ...filters,
      [e.target.name]: e.target.value
    });
  };

  const handleFormChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!user) {
      navigate('/login');
      return;
    }

    try {
      addRental({
        ...formData,
        price: parseFloat(formData.price),
        deposit: formData.deposit ? parseFloat(formData.deposit) : null
      }, user);

      setFormData({
        title: '',
        description: '',
        rentalItemType: 'textbook',
        rentalPeriod: 'daily',
        price: '',
        deposit: '',
        location: 'Campus'
      });
      setShowAddForm(false);
      fetchRentals();
    } catch (error) {
      console.error('Error adding rental:', error);
    }
  };

  const handleDelete = (rentalId) => {
    if (!user) return;

    if (window.confirm('Are you sure you want to delete this listing?')) {
      if (activeTab === 'admin' && isAdmin(user.email)) {
        adminDeleteListing('rentals', rentalId);
      } else {
        deleteRental(rentalId, user.id);
      }
      fetchRentals();
    }
  };

  const getItemTypeIcon = (itemType) => {
    const icons = {
      'textbook': '📚',
      'electronics': '💻',
      'equipment': '🔧',
      'furniture': '🪑',
      'vehicle': '🚗',
      'other': '📦'
    };
    return icons[itemType] || '📦';
  };

  return (
    <div className="rentals-page">
      {/* Header */}
      <div className="page-header">
        <button className="back-btn" onClick={() => navigate(-1)}>← Back</button>
        <h1 className="page-title">Rentals</h1>
        <p className="page-subtitle">Borrow items from fellow students</p>
      </div>

      {/* Tabs */}
      <div className="listing-tabs">
        <button
          className={`tab-btn ${activeTab === 'browse' ? 'active' : ''}`}
          onClick={() => setActiveTab('browse')}
        >
          Browse All
        </button>
        {user && (
          <button
            className={`tab-btn ${activeTab === 'my-listings' ? 'active' : ''}`}
            onClick={() => setActiveTab('my-listings')}
          >
            My Listings
          </button>
        )}
        {user && isAdmin(user.email) && (
          <button
            className={`tab-btn admin-tab ${activeTab === 'admin' ? 'active' : ''}`}
            onClick={() => setActiveTab('admin')}
          >
            Admin
          </button>
        )}
      </div>

      {/* Add Button */}
      {user && (
        <button className="fab-add-btn" onClick={() => setShowAddForm(!showAddForm)}>
          {showAddForm ? '✕' : '+'}
        </button>
      )}

      {/* Quick Add Form */}
      {showAddForm && user && (
        <div className="quick-add-form">
          <h3>List Item for Rent</h3>
          <form onSubmit={handleSubmit}>
            <div className="form-row">
              <input
                type="text"
                name="title"
                placeholder="What are you renting out?"
                value={formData.title}
                onChange={handleFormChange}
                required
              />
            </div>
            <div className="form-row">
              <textarea
                name="description"
                placeholder="Describe the item..."
                value={formData.description}
                onChange={handleFormChange}
                required
              />
            </div>
            <div className="form-row two-col">
              <select name="rentalItemType" value={formData.rentalItemType} onChange={handleFormChange}>
                <option value="textbook">Textbook</option>
                <option value="electronics">Electronics</option>
                <option value="equipment">Equipment</option>
                <option value="furniture">Furniture</option>
                <option value="vehicle">Vehicle</option>
                <option value="other">Other</option>
              </select>
              <select name="rentalPeriod" value={formData.rentalPeriod} onChange={handleFormChange}>
                <option value="hourly">Hourly</option>
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
                <option value="semester">Semester</option>
              </select>
            </div>
            <div className="form-row two-col">
              <div className="price-input">
                <span className="currency">$</span>
                <input
                  type="number"
                  name="price"
                  placeholder="Price"
                  value={formData.price}
                  onChange={handleFormChange}
                  required
                  min="0"
                  step="0.01"
                />
              </div>
              <div className="price-input">
                <span className="currency">$</span>
                <input
                  type="number"
                  name="deposit"
                  placeholder="Deposit (optional)"
                  value={formData.deposit}
                  onChange={handleFormChange}
                  min="0"
                  step="0.01"
                />
              </div>
            </div>
            <div className="form-row">
              <input
                type="text"
                name="location"
                placeholder="Pickup location"
                value={formData.location}
                onChange={handleFormChange}
              />
            </div>
            <div className="form-actions">
              <button type="button" className="btn-cancel" onClick={() => setShowAddForm(false)}>
                Cancel
              </button>
              <button type="submit" className="btn-submit">
                List for Rent
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Filters */}
      <div className="filters-bar">
        <div className="filter-group">
          <input
            type="text"
            name="search"
            placeholder="Search rentals..."
            value={filters.search}
            onChange={handleFilterChange}
            className="search-input"
          />
        </div>
        <div className="filter-group">
          <select name="rentalItemType" value={filters.rentalItemType} onChange={handleFilterChange}>
            <option value="">All Types</option>
            <option value="textbook">Textbooks</option>
            <option value="electronics">Electronics</option>
            <option value="equipment">Equipment</option>
            <option value="furniture">Furniture</option>
            <option value="vehicle">Vehicles</option>
          </select>
        </div>
        <div className="filter-group">
          <select name="rentalPeriod" value={filters.rentalPeriod} onChange={handleFilterChange}>
            <option value="">All Periods</option>
            <option value="hourly">Hourly</option>
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
            <option value="monthly">Monthly</option>
          </select>
        </div>
      </div>

      {/* Rentals Grid */}
      {loading ? (
        <div className="loading-message">Loading rentals...</div>
      ) : rentals.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">🔑</div>
          <h3>No rentals found</h3>
          <p>{activeTab === 'my-listings' ? 'You haven\'t listed any rentals yet.' : 'Be the first to list something for rent!'}</p>
          {user && (
            <button className="btn-primary" onClick={() => setShowAddForm(true)}>
              List for Rent
            </button>
          )}
        </div>
      ) : (
        <div className="rentals-grid">
          {rentals.map(rental => (
            <div key={rental._id} className="rental-card">
              <div className="rental-header">
                <span className="rental-type">
                  {getItemTypeIcon(rental.rentalItemType)} {rental.rentalItemType}
                </span>
                <div className="header-actions">
                  <span className="period-badge">{rental.rentalPeriod}</span>
                  {(activeTab === 'my-listings' || activeTab === 'admin') && (
                    <button
                      className="delete-btn"
                      onClick={() => handleDelete(rental._id)}
                    >
                      🗑️
                    </button>
                  )}
                </div>
              </div>

              <h3 className="rental-title">{rental.title}</h3>
              <p className="rental-description">
                {rental.description?.substring(0, 100)}...
              </p>

              <div className="rental-meta">
                <span className="rental-location">📍 {rental.location}</span>
                {rental.deposit && (
                  <span className="rental-deposit">${rental.deposit} deposit</span>
                )}
                <span className="rental-owner">By {rental.owner?.name || 'Student'}</span>
              </div>

              <div className="rental-footer">
                <div className="rental-price">
                  ${rental.price}
                  <span className="period-label">/{rental.rentalPeriod}</span>
                </div>
                <button className="btn-contact">Contact Owner</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Login Prompt */}
      {!user && (
        <div className="login-prompt">
          <p>Want to rent out your items?</p>
          <button className="btn-primary" onClick={() => navigate('/login')}>
            Sign in to list
          </button>
        </div>
      )}
    </div>
  );
}

export default Rentals;
